import logger from "@/helpers/logger";
import { notify } from "@kyvg/vue3-notification";
import { AxiosError } from "axios";
import { ErrorResponse } from "./types";

// Custom error class for API errors
export class ApiError extends Error {
  public readonly success?: boolean;
  public readonly code?: string;
  public readonly detail?: string;
  public readonly status?: number;
  public readonly name: string;
  public readonly data?: unknown;

  constructor({ message, success, code, detail, status, data }: ErrorResponse) {
    super(message);
    this.success = success;
    this.code = code;
    this.detail = detail;
    this.status = status;
    this.data = data;
    this.name = "ApiError";
    // Ensure proper prototype chain
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

type HandleError = {
  error: unknown;
  silent?: boolean;
  fallbackMessage?: string;
};

/**
 * Wraps an error into an ApiError for consistent error handling.
 */
export const wrapError = (
  error: unknown,
  defaultMessage: string,
  defaultCode: string
): ApiError => {
  if (
    error &&
    typeof error === "object" &&
    (error as ApiError).name === "ApiError"
  ) {
    return error as ApiError;
  }

  const axiosErr =
    error &&
    typeof error === "object" &&
    (error as AxiosError).name === "AxiosError"
      ? (error as AxiosError)
      : undefined;

  const axiosErrData = (axiosErr ? axiosErr?.response?.data : error) as
    | ErrorResponse
    | undefined;

  return new ApiError({
    message:
      axiosErrData?.detail ||
      axiosErrData?.message ||
      (error instanceof Error ? error?.message : defaultMessage),
    success: false,
    code: axiosErrData?.code || defaultCode,
    detail: axiosErrData?.detail,
    data: axiosErrData?.data,
    status: axiosErr?.response?.status,
  });
};

export const handleError = ({
  error,
  silent = false,
  fallbackMessage = "An error occurred",
}: HandleError): ApiError => {
  const apiError = wrapError(error, fallbackMessage, "UNKNOWN_ERROR");

  if (!silent) {
    notify({
      type: "error",
      text: apiError.message || apiError.detail || fallbackMessage,
    });
  }

  logger.error(apiError, apiError.message);

  return apiError;
};
